import React, { useEffect, useState } from "react"
import { FileText } from "lucide-react"

import { db } from "../../../utils/firebase"
import Button from "../../../components/ui/button/button"
import Badge from "../../../components/ui/badge/badge"
import { CreateQuoteModal } from "../../enquiry/_components/new-quote/new-quote"

/**
 * "Booked from quote" row on the booking Summary tab.
 *
 * Bookings created from an online quote carry a `booked_from` pointer plus a
 * staff-only copy of the quote at `bookings/{id}/booked_quote/{quoteID}` (the
 * originating enquiry is deleted on conversion). This lets the agent reopen the
 * exact itinerary the client booked - read-only, via the same quote builder
 * cancelled quotes use - so they know what to book in. Renders nothing for
 * bookings that didn't come from an online quote.
 */
export default function BookedFromQuote({ bookingID }) {
	const [bookedFrom, setBookedFrom] = useState(null)
	const [quote, setQuote] = useState(null)
	const [loading, setLoading] = useState(false)
	const [open, setOpen] = useState(false)

	useEffect(() => {
		if (!bookingID) return undefined
		let cancelled = false
		db.doc(`bookings/${bookingID}`)
			.get()
			.then((snap) => {
				if (!cancelled) setBookedFrom(snap.data()?.booked_from || null)
			})
			.catch((err) => console.error("Could not load booked_from:", err))
		return () => {
			cancelled = true
		}
	}, [bookingID])

	if (!bookedFrom?.quote_id) return null

	const openQuote = async () => {
		if (loading) return
		// Lazily load the saved quote copy, then open the builder read-only.
		if (quote) {
			setOpen(true)
			return
		}
		setLoading(true)
		try {
			const snap = await db.doc(`bookings/${bookingID}/booked_quote/${bookedFrom.quote_id}`).get()
			if (snap.exists) {
				setQuote({ id: snap.id, ...snap.data() })
				setOpen(true)
			} else {
				console.warn("Booked quote copy not found for booking", bookingID)
			}
		} catch (err) {
			console.error("Could not load booked quote:", err)
		} finally {
			setLoading(false)
		}
	}

	return (
		<tr>
			<td>Booked from quote</td>
			<td>
				<div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
					<Button
						type="secondary"
						small
						label="View booked quote"
						icon={<FileText size={15} />}
						loading={loading}
						loadingText="Loading..."
						onClick={openQuote}
						disabled={loading}
					/>
					{bookedFrom.quote_reference ? <Badge label={bookedFrom.quote_reference} type="INFO" /> : null}
				</div>

				{open && quote && (
					<CreateQuoteModal
						seed={null}
						enquiryID={bookedFrom.enquiry_id}
						draftId={quote.id}
						initialDraft={quote}
						readOnly={true}
						onClose={() => setOpen(false)}
					/>
				)}
			</td>
		</tr>
	)
}
